/**
 * Phase 3E: Reconciliation Progress Bar
 * 
 * Visual indicator showing X of Y CPTs reconciled
 * with status breakdown.
 */

import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  CheckCircle2,
  Clock,
  AlertTriangle,
  XCircle,
  FileCheck,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { ReconciliationProgress } from '@/hooks/useClarificationWorkflow';

interface ReconciliationProgressBarProps {
  progress: ReconciliationProgress;
  showDetails?: boolean;
  compact?: boolean;
}

export function ReconciliationProgressBar({
  progress,
  showDetails = true,
  compact = false,
}: ReconciliationProgressBarProps) {
  const {
    totalCpts,
    cptsWithSufficientDocs,
    cptsWithClarificationsPending,
    cptsConfirmed,
    cptsBillable,
    progressPercent,
    allResolved,
    canExport,
  } = progress;
  
  const pendingCount = totalCpts - cptsConfirmed;
  
  if (compact) {
    return (
      <div className="flex items-center gap-3">
        <Progress value={progressPercent} className="h-2 flex-1" />
        <span className="text-sm font-medium whitespace-nowrap">
          {cptsConfirmed}/{totalCpts}
        </span>
        {canExport && (
          <CheckCircle2 className="w-4 h-4 text-success shrink-0" />
        )}
      </div>
    );
  }
  
  return (
    <Card className={cn(
      "border",
      canExport ? "border-success/50 bg-success/5" : "border-muted"
    )}>
      <CardContent className="p-4 space-y-3">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileCheck className={cn(
              "w-5 h-5",
              canExport ? "text-success" : "text-muted-foreground"
            )} />
            <span className="font-medium">Reconciliation Progress</span>
          </div>
          
          <Badge 
            variant={canExport ? "default" : "secondary"}
            className={cn(
              canExport && "bg-success text-success-foreground"
            )}
          >
            {progressPercent}% Complete
          </Badge>
        </div>
        
        {/* Progress bar */}
        <div className="space-y-1">
          <Progress value={progressPercent} className="h-3" />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>{cptsConfirmed} of {totalCpts} CPTs confirmed</span>
            {cptsBillable > 0 && (
              <span className="text-success font-medium">
                {cptsBillable} billable
              </span>
            )}
          </div>
        </div>
        
        {/* Status breakdown */}
        {showDetails && (
          <div className="flex flex-wrap gap-2 pt-1">
            {/* Sufficient docs */}
            <Badge variant="outline" className="text-xs bg-success/10 text-success border-success/30">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              {cptsWithSufficientDocs} Sufficient Docs
            </Badge>
            
            {/* Pending clarifications */}
            {cptsWithClarificationsPending > 0 && (
              <Badge variant="outline" className="text-xs bg-warning/10 text-warning border-warning/30">
                <AlertTriangle className="w-3 h-3 mr-1" />
                {cptsWithClarificationsPending} Need Clarification
              </Badge>
            )}
            
            {/* Pending confirmations */}
            {pendingCount > 0 && (
              <Badge variant="outline" className="text-xs bg-muted text-muted-foreground">
                <Clock className="w-3 h-3 mr-1" />
                {pendingCount} Awaiting Confirmation
              </Badge>
            )}
            
            {/* Billable */}
            {cptsBillable > 0 && (
              <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/30">
                <FileCheck className="w-3 h-3 mr-1" />
                {cptsBillable} Billable
              </Badge>
            )}
          </div>
        )}
        
        {/* Export status */}
        {canExport ? (
          <div className="flex items-center gap-2 text-sm text-success">
            <CheckCircle2 className="w-4 h-4" />
            <span>Ready for EHR export</span>
          </div>
        ) : !allResolved ? (
          <div className="flex items-center gap-2 text-sm text-warning">
            <AlertTriangle className="w-4 h-4" />
            <span>Resolve all clarifications and confirmations to enable export</span>
          </div>
        ) : cptsBillable === 0 ? (
          <div className="flex items-center gap-2 text-sm text-destructive">
            <XCircle className="w-4 h-4" />
            <span>No billable CPTs — export blocked</span>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
